import React from "react"
import { Box, TextField } from "@material-ui/core"
import { useDebounce } from "../../hooks/use-debounce"

export type ManualEntryValue = {
  hours: number
  minutes: number
}

type ManualEntryProps = {
  hoursLabel: string
  minutesLabel: string
  hours: number
  hoursError?: string
  minutes: number
  onChange: (value: ManualEntryValue) => void | Promise<boolean>
  minutesError?: string
}

const toNumericString = (value: string) => value.replace(/\D/g, "")
const toNumber = (value: string) => (value === "" ? 0 : Number(value))

const ManualEntryFields = ({
  hoursLabel,
  minutesLabel,
  hours,
  hoursError,
  minutes,
  onChange,
  minutesError,
}: ManualEntryProps) => {
  const [hoursInput, setHoursInput] = React.useState(String(hours))
  const [minutesInput, setMinutesInput] = React.useState(String(minutes))
  const [isDirty, setIsDirty] = React.useState(false)
  const [hasAuthoritativeLocalValue, setHasAuthoritativeLocalValue] = React.useState(false)
  const commitSequence = React.useRef(0)
  const isMounted = React.useRef(true)

  const commitChange = React.useCallback(
    (value: ManualEntryValue) => {
      const sequence = ++commitSequence.current
      setHasAuthoritativeLocalValue(true)
      const result = onChange(value)
      setIsDirty(false)

      if (!result || typeof result.then !== "function") {
        setHasAuthoritativeLocalValue(false)
        return
      }

      result.then((isSuccessful) => {
        if (!isMounted.current || commitSequence.current !== sequence || isSuccessful) return
        setHasAuthoritativeLocalValue(false)
      })
    },
    [onChange]
  )

  const { debouncedFn: debouncedOnChange, flush } = useDebounce(commitChange, 500, {
    trailing: true,
    flushOnUnmount: true,
  })

  React.useEffect(() => {
    if (isDirty || hasAuthoritativeLocalValue) return

    if (toNumber(hoursInput) !== hours) setHoursInput(String(hours))
    if (toNumber(minutesInput) !== minutes) setMinutesInput(String(minutes))
    // Local input changes must not be overwritten before the parent value changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAuthoritativeLocalValue, hours, isDirty, minutes])

  React.useEffect(() => {
    window.addEventListener?.("beforeunload", flush)

    return () => {
      isMounted.current = false
      window.removeEventListener?.("beforeunload", flush)
    }
  }, [flush])

  const handleHoursChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextHours = toNumericString(event.target.value)
    setIsDirty(true)
    setHoursInput(nextHours)
    debouncedOnChange({ hours: toNumber(nextHours), minutes: toNumber(minutesInput) })
  }

  const handleMinutesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextMinutes = toNumericString(event.target.value)
    setIsDirty(true)
    setMinutesInput(nextMinutes)
    debouncedOnChange({ hours: toNumber(hoursInput), minutes: toNumber(nextMinutes) })
  }

  return (
    <Box
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
        flush()
      }}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ mr: 1, width: "50%" }}>
        <TextField
          label={hoursLabel}
          id="manual-entry-hours"
          name="hours"
          type="text"
          value={hoursInput}
          onChange={handleHoursChange}
          onFocus={(event) => event.target.select()}
          onKeyDown={(e) => e.stopPropagation()}
          error={Boolean(hoursError)}
          helperText={hoursError}
          variant="outlined"
          margin="dense"
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          fullWidth
        />
      </Box>
      <Box sx={{ ml: 1, width: "50%" }}>
        <TextField
          label={minutesLabel}
          id="manual-entry-minutes"
          name="minutes"
          type="text"
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          value={minutesInput}
          onChange={handleMinutesChange}
          onFocus={(event) => event.target.select()}
          onKeyDown={(e) => e.stopPropagation()}
          error={Boolean(minutesError)}
          helperText={minutesError}
          variant="outlined"
          margin="dense"
          fullWidth
        />
      </Box>
    </Box>
  )
}

export default ManualEntryFields
